import { Component, Input, OnInit, inject } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	ButtonIconComponent,
	FormGroupComponent,
	LinkComponent,
	ITextEditor,
	TextEditorComponent,
} from '@tecalisdev/infinity';
import { ReactiveFormsService } from '../../../../shared/services/reactive-forms.service';
import { WorkflowsHelperService } from '../../../services/workflows-helper.service';
import { extractWorkflowRichTextHtml } from '../../../utils/workflow-locale-text.util';
import {
	assignFormStepOptionOrderValues,
	createFormStepRichTextOptionGroup,
	patchRichTextOptionLabels,
} from '../form-step-options.helper';
import {
	formStepOptionLabelErrorMessage,
	formStepOptionLabelHasError,
	getFormStepOptionLabelControl,
} from '../form-step-option-locale-ui.util';

@Component({
	selector: 'app-form-step-rich-text-options-editor',
	imports: [
		ReactiveFormsModule,
		TranslatePipe,
		FormGroupComponent,
		TextEditorComponent,
		ButtonIconComponent,
		LinkComponent,
	],
	templateUrl: './form-step-rich-text-options-editor.component.html',
	styleUrl: './form-step-rich-text-options-editor.component.scss',
})
export class FormStepRichTextOptionsEditorComponent implements OnInit {
	private workflowsHelper = inject(WorkflowsHelperService);
	private trans = inject(TranslateService);
	private reactiveFormService = inject(ReactiveFormsService);

	@Input({ required: true }) fieldGroup!: FormGroup;
	@Input() selectedLocaleId = 'es';

	private readonly editorValueCache = new Map<string, ITextEditor>();

	ngOnInit(): void {
		patchRichTextOptionLabels(this.optionsArray);
		this.editorValueCache.clear();
	}

	public trackRichOption(optionIndex: number): string {
		return `${this.selectedLocaleId}-${optionIndex}`;
	}

	public get optionsArray(): FormArray {
		return this.fieldGroup.get('options') as FormArray;
	}

	public getOptionLabelControl(optionIndex: number): FormControl {
		return getFormStepOptionLabelControl(this.optionsArray, optionIndex, this.selectedLocaleId);
	}

	public readEditorValue(optionIndex: number): ITextEditor {
		const html = extractWorkflowRichTextHtml(this.getOptionLabelControl(optionIndex).value);
		const cacheKey = this.trackRichOption(optionIndex);
		const cached = this.editorValueCache.get(cacheKey);

		if (cached && cached.html === html) {
			return cached;
		}

		const next = { html, attachments: [] as never[] };
		this.editorValueCache.set(cacheKey, next);
		return next;
	}

	public onEditorValueChange(optionIndex: number, value: ITextEditor): void {
		const control = this.getOptionLabelControl(optionIndex);
		const html = extractWorkflowRichTextHtml(value);
		const current = extractWorkflowRichTextHtml(control.value);

		if (html === current) {
			return;
		}

		control.setValue(html, { emitEvent: false });
		control.markAsDirty();
		control.updateValueAndValidity({ emitEvent: false });
		this.editorValueCache.set(this.trackRichOption(optionIndex), { html, attachments: [] as never[] });
	}

	public optionLabelHasError(optionIndex: number): boolean {
		return formStepOptionLabelHasError(
			this.reactiveFormService,
			this.optionsArray,
			optionIndex,
			this.selectedLocaleId,
			true,
		);
	}

	public optionLabelErrorMessage(optionIndex: number): string {
		return formStepOptionLabelErrorMessage(
			this.reactiveFormService,
			this.optionsArray,
			optionIndex,
			this.selectedLocaleId,
			true,
			this.trans.instant('ERRORS.REQUIRED'),
		);
	}

	public addOption(): void {
		this.optionsArray.push(createFormStepRichTextOptionGroup(this.workflowsHelper));
		assignFormStepOptionOrderValues(this.optionsArray);
	}

	public removeOption(optionIndex: number): void {
		if (this.optionsArray.length <= 1) {
			return;
		}

		this.optionsArray.removeAt(optionIndex);
		assignFormStepOptionOrderValues(this.optionsArray);
	}
}
